/**
 * Career copy helpers — presentation only.
 *
 * The registry's list fields were flattened from prose, and the flattening
 * split on commas without respecting brackets. That leaves debris like
 * "Design CubeSat subsystems using COTS components (EPS" / "OBC" / "comms" /
 * "payload)". These helpers repair the fragments at render time. Nothing here
 * changes the stored evidence — the shard files stay exactly as generated.
 */

/** Balance of "(" minus ")" in a string. */
function parenDelta(s: string): number {
  let d = 0;
  for (const ch of s) {
    if (ch === "(") d++;
    else if (ch === ")") d--;
  }
  return d;
}

/**
 * Re-join list items that were split inside a bracketed aside, so
 * ["… (EPS", "OBC", "comms", "payload)"] becomes
 * ["… (EPS, OBC, comms, payload)"].
 */
export function mergeBracketFragments(items: string[]): string[] {
  const out: string[] = [];
  let open = 0;
  for (const raw of items) {
    const item = raw.trim();
    if (!item) continue;
    if (open > 0 && out.length) {
      out[out.length - 1] = `${out[out.length - 1]}, ${item}`;
      open += parenDelta(item);
      if (open < 0) open = 0;
      continue;
    }
    out.push(item);
    open = Math.max(0, parenDelta(item));
  }
  return out;
}

/**
 * Words whose spelling and sound disagree, so the letter test gets the
 * article wrong. Keys are lowercase word starts.
 */
const VOWEL_SOUND_CONSONANT = /^(hour|honest|honour|honor|heir)/i;
const CONSONANT_SOUND_VOWEL = /^(uni|use|user|usual|utili|utilis|utiliz|eu|one[a-z-]*|ubiquit)/i;

/** Letters that are read out and so begin with a vowel sound: F L M N R S X. */
const VOWEL_SOUND_LETTERS = /^[AEFHILMNORSX]/;

/** True when `word` should take "an" rather than "a". */
export function takesAn(word: string): boolean {
  const w = word.replace(/^[^A-Za-z0-9]+/, "");
  if (!w) return false;
  if (VOWEL_SOUND_CONSONANT.test(w)) return true;
  if (CONSONANT_SOUND_VOWEL.test(w)) return false;
  // Initialism such as "AI", "MBA", "NGO" — read letter by letter.
  if (/^[A-Z]{2,}/.test(w) && !/^[A-Z][a-z]/.test(w)) return VOWEL_SOUND_LETTERS.test(w);
  return /^[aeiouAEIOU]/.test(w);
}

/**
 * Repair "a"/"an" so the article agrees with the sound of the next word.
 * The registry's prose is template-generated, so rows read "A Academic
 * Registrar …" or "an Business Analyst …". Fixed at render time only.
 */
export function fixArticles(text: string): string {
  return text.replace(/\b([Aa]|[Aa]n)(\s+)([^\s]+)/g, (match, article: string, gap: string, next: string) => {
    const want = takesAn(next);
    const isAn = article.toLowerCase() === "an";
    if (want === isAn) return match;
    const upper = article[0] === "A";
    const fixed = want ? (upper ? "An" : "an") : upper ? "A" : "a";
    return `${fixed}${gap}${next}`;
  });
}

/** Sentence case a fragment without touching acronyms already in caps. */
export function tidyItem(text: string): string {
  const s = fixArticles(text.replace(/\s+/g, " ").trim().replace(/[.;,]+$/, ""));
  if (!s) return s;
  if (s.slice(0, 2).toUpperCase() === s.slice(0, 2)) return s; // acronym start
  return s[0].toUpperCase() + s.slice(1);
}


/**
 * Split a prose blob into bullets. Splits on newlines, bullet glyphs and
 * semicolons — never on commas, because commas live inside the content.
 */
export function splitBullets(text: string | null | undefined): string[] {
  if (!text || !text.trim()) return [];
  return mergeListFragments(
    mergeBracketFragments(
      text
        .split(/\n+|(?:[•·–—]\s+)|(?:;\s+)|(?:\|\s*)/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ).map(tidyItem);
}

/** First N comma/semicolon separated names from a blob, for chips. */
export function splitNames(text: string | null | undefined, limit = 12): string[] {
  if (!text || !text.trim()) return [];
  return mergeBracketFragments(
    text.split(/\n+|[•·]|;|,/).map((s) => s.trim()).filter(Boolean),
  ).slice(0, limit);
}

/**
 * Verbs the registry's responsibility lines begin with. Used to tell a real
 * list item ("Produce educational videos") from prose debris left behind when
 * a sentence was flattened on its commas ("Infographics").
 */
const HEAD_VERBS = new Set([
  "advise","analyse","analyze","apply","assess","assist","attend","audit","build","carry",
  "check","collaborate","collect","communicate","compile","complete","conduct","configure",
  "contribute","coordinate","create","define","deliver","design","develop","diagnose",
  "document","draft","drive","engage","ensure","establish","evaluate","execute","explain",
  "facilitate","follow","gather","generate","guide","handle","help","identify","implement",
  "improve","inspect","install","interpret","investigate","keep","lead","liaise","maintain",
  "make","manage","measure","meet","mentor","monitor","negotiate","observe","operate",
  "organise","organize","oversee","own","participate","perform","plan","prepare","present",
  "process","produce","promote","protect","provide","publish","record","recruit","reduce",
  "report","research","resolve","respond","review","run","schedule","secure","select","set",
  "share","solve","specify","staff","stay","supervise","support","take","teach","test",
  "track","train","translate","troubleshoot","understand","update","use","validate","verify",
  "work","write",
]);

const CONJ_START = /^(and|or|&|as well as|including|plus)\s+/i;

/** Lowercase the first letter unless the word is an acronym or proper noun-ish. */
function joinCase(text: string): string {
  if (/^[A-Z]{2,}/.test(text) || /^[A-Z][A-Za-z]*[A-Z]/.test(text)) return text;
  return text[0].toLowerCase() + text.slice(1);
}

function firstWord(s: string): string {
  return s.replace(/^[^A-Za-z]+/, "").split(/[\s,/]/)[0].toLowerCase();
}

/** A short line with no leading verb is debris, not its own responsibility. */
function isDebris(item: string): boolean {
  if (HEAD_VERBS.has(firstWord(item))) return false;
  return item.split(/\s+/).length <= 3;
}

/**
 * Re-join list items that were split out of one sentence, so
 * ["Organize space exhibitions", "Workshops", "And competitions …"] reads
 * "Organize space exhibitions, workshops, and competitions …".
 */
export function mergeListFragments(items: string[]): string[] {
  const out: string[] = [];
  for (const raw of items) {
    const item = raw.trim().replace(/\s+/g, " ");
    if (!item) continue;
    const conj = CONJ_START.exec(item);
    if (out.length && conj) {
      const tail = item.slice(conj[0].length).trim();
      if (tail) {
        const word = conj[1].toLowerCase();
        const glue = word === "&" ? "and" : word;
        out[out.length - 1] = `${out[out.length - 1]}, ${glue} ${joinCase(tail)}`;
        continue;
      }
    }
    if (out.length && isDebris(item)) {
      out[out.length - 1] = `${out[out.length - 1]}, ${joinCase(item)}`;
      continue;
    }
    out.push(item);
  }
  return out;
}

/** Full repair pass for a prose-derived list: brackets, fragments, grammar. */
export function tidyList(items: string[] | null | undefined): string[] {
  if (!items?.length) return [];
  return mergeListFragments(mergeBracketFragments(items)).map(tidyItem);
}

/**
 * Repair pass for lists that are genuinely short labels — skills, employers,
 * tools. These are never flattened prose, so fragment merging must NOT run:
 * "Leadership" and "Sony" are whole entries, not debris.
 */
export function tidyChips(items: string[] | null | undefined): string[] {
  if (!items?.length) return [];
  return mergeBracketFragments(items).map((s) => s.replace(/\s+/g, " ").trim().replace(/[.;,]+$/, "")).filter(Boolean);
}
